import { useTranslation } from 'react-i18next';
import { Bar, BarChart, CartesianGrid, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { alignStagesToColumns } from '@/lib/funnelTable';

import type { FunnelReport } from '@/types/report.types';

interface FunnelChartProps {
  data: FunnelReport;
}

const STAGE_COLORS: Record<string, string> = {
  submitted: '#3b82f6',
  video_pending: '#8b5cf6',
  video_completed: '#6366f1',
  under_review: '#f59e0b',
  approved: '#22c55e',
  rejected: '#ef4444',
  waitlist: '#f97316',
};

export function FunnelChart({ data }: FunnelChartProps) {
  const { t } = useTranslation();

  const chartData = data.overall.map((stage) => ({
    name: t(`reports.funnel.stages.${stage.stage}`),
    count: stage.count,
    percentage: stage.percentage,
    stage: stage.stage,
  }));

  return (
    <div className='rounded-lg bg-white p-6 shadow'>
      <div className='flex items-center justify-between mb-4'>
        <h3 className='text-lg font-semibold text-gray-900'>{t('reports.funnel.title')}</h3>
        <div className='text-right'>
          <p className='text-2xl font-bold text-gray-900'>{data.totalApplications}</p>
          <p className='text-xs text-gray-500'>{t('reports.funnel.totalApplications')}</p>
        </div>
      </div>

      <ResponsiveContainer width='100%' height={300}>
        <BarChart data={chartData} layout='vertical' margin={{ top: 0, right: 30, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray='3 3' horizontal={false} />
          <XAxis type='number' />
          <YAxis type='category' dataKey='name' width={120} tick={{ fontSize: 12 }} />
          <Tooltip
            formatter={(value, _name, props) => [
              `${value} (${(props.payload as { percentage: number }).percentage}%)`,
              '',
            ]}
          />
          <Bar dataKey='count' radius={[0, 4, 4, 0]}>
            {chartData.map((entry) => (
              <Cell key={entry.stage} fill={STAGE_COLORS[entry.stage] ?? '#94a3b8'} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      {/* Breakdown tables */}
      {data.byRole.length > 0 && (
        <div className='mt-6'>
          <h4 className='text-sm font-medium text-gray-700 mb-2'>{t('reports.funnel.byRole')}</h4>
          <div className='overflow-x-auto'>
            <table className='min-w-full text-xs'>
              <thead>
                <tr className='border-b border-gray-200'>
                  <th className='py-2 pr-4 text-left font-medium text-gray-500'>Role</th>
                  {data.overall.map((s) => (
                    <th key={s.stage} className='py-2 px-2 text-right font-medium text-gray-500'>
                      {t(`reports.funnel.stages.${s.stage}`)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.byRole.map((row) => (
                  <tr key={row.category} className='border-b border-gray-100'>
                    <td className='py-2 pr-4 font-medium text-gray-700'>
                      {row.category === 'unassigned'
                        ? t('reports.funnel.unassigned')
                        : row.category.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
                    </td>
                    {alignStagesToColumns(data.overall, row.stages).map((cell) => (
                      <td key={cell.stage} className='py-2 px-2 text-right text-gray-600'>
                        {cell.count}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
